import { Component } from 'preact';
import RuneSpinner from './RuneSpinner';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  BG_ATMOSPHERE, VIGNETTE, DIALOG_STYLE, PANEL_BG,
  FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { getLocalApiOrigin } from '../utils/localApi';
import { t } from '../localization/t';

/**
 * First-run asset download screen.
 * Pre-downloads all card images from CDN on first launch.
 * Shows a medieval-themed progress screen.
 *
 * Receives the explicit list of slugs missing from disk via the
 * `missingSlugs` prop (computed by checkAssetDownload in app.jsx). On a
 * fresh install that's the entire catalog (~1100 slugs); after a new
 * set ships it's just the delta. Progress is reported against this
 * missing count so the UI always reads as "X of N remaining".
 */
export default class FirstRunDownload extends Component {
  constructor(props) {
    super(props);
    const target = props.missingSlugs?.length || 0;
    this.state = {
      phase: target > 0 ? 'downloading' : 'complete',
      target, // how many slugs this gate is responsible for fetching
      done: 0, // how many of those `target` slugs are now on disk
      failed: 0,
      errorMessage: null,
    };
    this.pollTimer = null;
  }

  componentDidMount() {
    if (this.state.target === 0) {
      setTimeout(() => this.props.onComplete?.(), 300);
      return;
    }
    this.startDownload();
  }

  componentWillUnmount() {
    clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  async startDownload() {
    clearInterval(this.pollTimer);
    this.pollTimer = null;

    try {
      const api = getLocalApiOrigin();
      const downloadRes = await fetch(`${api}/api/assets/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slugs: this.props.missingSlugs }),
      });
      if (!downloadRes.ok) {
        throw new Error(`Asset download request failed (HTTP ${downloadRes.status})`);
      }

      const downloadStatus = await downloadRes.json();
      const reportedTotal = Number(downloadStatus.total);
      const target = Number.isFinite(reportedTotal) && reportedTotal >= 0
        ? reportedTotal
        : this.state.target;

      if (downloadStatus.status === 'complete') {
        this.setState({ phase: 'complete', target, done: target, failed: 0, errorMessage: null });
        setTimeout(() => this.props.onComplete?.(), 500);
        return;
      }

      this.setState({ phase: 'downloading', target, done: 0, failed: 0, errorMessage: null });
      this.pollTimer = setInterval(() => this.pollProgress(), 500);
    } catch (err) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.setState({ phase: 'error', errorMessage: err.message });
    }
  }

  async pollProgress() {
    try {
      const api = getLocalApiOrigin();
      const res = await fetch(`${api}/api/assets/status`);
      if (!res.ok) {
        throw new Error(`Asset status request failed (HTTP ${res.status})`);
      }

      const status = await res.json();
      const downloaded = Number(status.downloaded) || 0;
      const failed = Number(status.failed) || 0;

      this.setState({
        done: downloaded + failed,
        failed,
      });

      if (status.done) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;

        if (failed > 0) {
          this.setState({
            phase: 'error',
            errorMessage: t('{failed} of {total} assets failed to download. Retry to fetch the remaining files.', { failed, total: this.state.target }),
          });
          return;
        }

        this.setState({ phase: 'complete', errorMessage: null });
        setTimeout(() => this.props.onComplete?.(), 1000);
      }
    } catch (err) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.setState({ phase: 'error', errorMessage: err.message });
    }
  }

  render() {
    const { phase, target, done, failed, errorMessage } = this.state;
    const progress = target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 100;
    const remaining = Math.max(0, target - done);

    return (
      <div className="fixed inset-0 z-[95] grid place-items-center" style={{ background: BG_ATMOSPHERE }}>
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative w-full max-w-[32cqw] p-8" style={{ ...DIALOG_STYLE }}>
          <FourCorners />

          <div className="text-center">
            <img src="/valkenhall-logo.png" alt="Valkenhall" className="h-16 mx-auto mb-6" draggable={false} />

            {phase === 'checking' && (
              <>
                <div className="flex justify-center mb-4">
                  <RuneSpinner size={56} />
                </div>
                <h2 className="text-xl font-bold arena-heading mb-2" style={{ color: TEXT_PRIMARY }}>{t('Preparing the Arena')}</h2>
                <p className="text-sm" style={{ color: TEXT_MUTED }}>{t('Checking game assets...')}</p>
              </>
            )}

            {phase === 'downloading' && (
              <>
                <h2 className="text-xl font-bold arena-heading mb-2" style={{ color: TEXT_PRIMARY }}>{t('Downloading Game Assets')}</h2>
                <p className="text-sm mb-4" style={{ color: TEXT_MUTED }}>
                  {t('Please wait while game assets are being downloaded.')}
                </p>

                {/* Progress bar */}
                <div
                  className="w-full h-3 rounded-full overflow-hidden mb-3"
                  style={{ background: `${GOLD} 0.08)`, border: `1px solid ${GOLD} 0.12)` }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #8b6914, #d4a843, #c49a38)',
                      boxShadow: '0 0 8px rgba(212,168,67,0.3)',
                    }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs" style={{ color: TEXT_MUTED }}>
                  <span>{t('{done} / {total} assets', { done: Math.min(done, target), total: target })}</span>
                  <span className="font-bold" style={{ color: ACCENT_GOLD }}>{progress}%</span>
                </div>

                {remaining > 0 && (
                  <p className="text-xs mt-3" style={{ color: TEXT_MUTED }}>
                    {t('{n} remaining', { n: remaining.toLocaleString() })}
                    {failed > 0 && <span style={{ color: '#c45050' }}> · {t('{n} failed', { n: failed })}</span>}
                  </p>
                )}
              </>
            )}

            {phase === 'complete' && (
              <>
                <div className="text-4xl mb-4">⚔️</div>
                <h2 className="text-xl font-bold arena-heading mb-2" style={{ color: TEXT_PRIMARY }}>{t('Ready for Battle')}</h2>
                <p className="text-sm" style={{ color: TEXT_MUTED }}>{t('All assets downloaded. Entering the arena...')}</p>
              </>
            )}

            {phase === 'error' && (
              <>
                <h2 className="text-xl font-bold arena-heading mb-2" style={{ color: '#c45050' }}>{t('Download Failed')}</h2>
                <p className="text-sm mb-4" style={{ color: TEXT_MUTED }}>{errorMessage || t('Could not download game assets.')}</p>
                <div className="flex justify-center gap-3">
                  <button
                    type="button"
                    className="px-5 py-2 text-sm font-semibold arena-heading cursor-pointer transition-all"
                    style={{
                      background: 'linear-gradient(180deg, rgba(212,168,67,0.9) 0%, rgba(160,120,40,0.9) 100%)',
                      border: '1px solid rgba(228,200,100,0.6)',
                      borderRadius: 'var(--radius-md)',
                      color: '#1a1408',
                    }}
                    onClick={() => {
                      this.setState({ phase: 'downloading', errorMessage: null, done: 0, failed: 0 });
                      this.startDownload();
                    }}
                  >
                    {t('Retry')}
                  </button>
                  <button
                    type="button"
                    className="px-5 py-2 text-sm cursor-pointer transition-all"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0.12) 100%)',
                      border: `1px solid ${GOLD} 0.3)`,
                      borderRadius: 'var(--radius-md)',
                      color: TEXT_BODY,
                    }}
                    onClick={() => this.props.onComplete()}
                  >
                    {t('Skip for Now')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}
